// src/app/hooks/useBriefingInsights.ts
// Separate hook for briefing insights so it is NEVER called on page load.
// - fetchInsights() loads from DB cache (no LLM)
// - refreshInsights() calls the backend with force_refresh=true (calls LLM)

import { useState, useCallback } from 'react';
import { getBriefingInsights } from '../../services/prompt-tracking/prompt-tracking-api';
import type { BriefingInsight } from '../../types/prompt-tracking-types';

interface UseBriefingInsightsResult {
  insights: BriefingInsight[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasFetched: boolean;
  fetchInsights: () => void;       // load from DB cache (no LLM)
  refreshInsights: () => void;     // force LLM regeneration
}

export function useBriefingInsights(): UseBriefingInsightsResult {
  const [insights, setInsights] = useState<BriefingInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchInsights = useCallback(() => {
    setIsLoading(true);
    getBriefingInsights(false)           // force_refresh=false → DB only, no LLM
      .then((data) => {
        setInsights(data);
        setHasFetched(true);
      })
      .catch(() => setInsights([]))
      .finally(() => setIsLoading(false));
  }, []);

  const refreshInsights = useCallback(() => {
    setIsRefreshing(true);
    getBriefingInsights(true)            // force_refresh=true → calls Claude
      .then((data) => {
        setInsights(data);
        setHasFetched(true);
      })
      .catch(() => {})
      .finally(() => setIsRefreshing(false));
  }, []);

  return { insights, isLoading, isRefreshing, hasFetched, fetchInsights, refreshInsights };
}